import React, { useEffect, useState } from "react";
import { Check, Search, Star, X } from "lucide-react";
import {
  MobileCategoryFilterSection,
  mobileCategoryFilterSections,
} from "../CategoryListingTypes";

interface CategoryMobileFilterSheetProps {
  isOpen: boolean;
  onClose: () => void;
  activeSection: MobileCategoryFilterSection;
  onSelectSection: (section: MobileCategoryFilterSection) => void;
  getMobileFilterCount: (section: MobileCategoryFilterSection) => number;
  totalProductsCount: number;
  onClearAll: () => void;

  // Filter values and handlers
  sort: string;
  onSortChange: (sort: string) => void;

  minPrice?: number;
  maxPrice?: number;
  priceRange?: { min: number; max: number };
  onSetPrice: (min?: number, max?: number) => void;

  freeDelivery: boolean;
  fastDelivery: boolean;
  nextDayDelivery: boolean;
  deliveryCounts?: {
    freeDelivery?: number;
    fastDelivery?: number;
    nextDayDelivery?: number;
  };
  onToggleDelivery: (type: "free" | "fast" | "nextDay") => void;

  brands: { _id: string; name: string; count: number }[];
  selectedBrands: string[];
  onToggleBrand: (brandId: string) => void;

  ratingBuckets: { minRating: number; count: number; label?: string }[];
  selectedRatings: number[];
  onToggleRating: (rating: number) => void;

  discountBuckets: { minDiscount: number; label: string; count: number }[];
  selectedDiscounts: number[];
  onToggleDiscount: (discount: number) => void;

  colors: { value: string; count: number }[];
  selectedColors: string[];
  onToggleColor: (color: string) => void;

  materials: { value: string; count: number }[];
  selectedMaterials: string[];
  onToggleMaterial: (material: string) => void;
}

const sortOptions = [
  { value: "newest", label: "Featured" },
  { value: "price_asc", label: "Price: Low to High" },
  { value: "price_desc", label: "Price: High to Low" },
  { value: "rating", label: "Customer Rating" },
  { value: "discount", label: "Discount" },
];

const pricePresets = [
  { id: "under-500", label: "Under ₹500", min: undefined, max: 500 },
  { id: "500-1000", label: "₹500 - ₹1,000", min: 500, max: 1000 },
  { id: "1000-2500", label: "₹1,000 - ₹2,500", min: 1000, max: 2500 },
  { id: "2500-5000", label: "₹2,500 - ₹5,000", min: 2500, max: 5000 },
  { id: "above-5000", label: "Above ₹5,000", min: 5000, max: undefined },
];

export const CategoryMobileFilterSheet: React.FC<CategoryMobileFilterSheetProps> = ({
  isOpen,
  onClose,
  activeSection,
  onSelectSection,
  getMobileFilterCount,
  totalProductsCount,
  onClearAll,
  sort,
  onSortChange,
  minPrice,
  maxPrice,
  priceRange,
  onSetPrice,
  freeDelivery,
  fastDelivery,
  nextDayDelivery,
  deliveryCounts,
  onToggleDelivery,
  brands,
  selectedBrands,
  onToggleBrand,
  ratingBuckets,
  selectedRatings,
  onToggleRating,
  discountBuckets,
  selectedDiscounts,
  onToggleDiscount,
  colors,
  selectedColors,
  onToggleColor,
  materials,
  selectedMaterials,
  onToggleMaterial,
}) => {
  const [searchQuery, setSearchQuery] = useState("");

  // Clear local search when changing active section
  useEffect(() => {
    setSearchQuery("");
  }, [activeSection]);

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const matchesSearch = (text: string) => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return true;
    return text.toLowerCase().includes(q);
  };

  const activeSectionConfig = mobileCategoryFilterSections.find(
    (s) => s.id === activeSection
  );

  const renderOptions = () => {
    switch (activeSection) {
      case "sort":
        return (
          <div className="category-mobile-sheet-options">
            {sortOptions
              .filter((opt) => matchesSearch(opt.label))
              .map((opt) => {
                const isSelected = sort === opt.value;
                return (
                  <label
                    key={opt.value}
                    className={`category-mobile-sheet-option ${
                      isSelected ? "active" : ""
                    }`}
                  >
                    <input
                      type="radio"
                      name="category-mobile-sort"
                      checked={isSelected}
                      onChange={() => onSortChange(opt.value)}
                    />
                    <span>{opt.label}</span>
                    {isSelected && <Check size={16} strokeWidth={2.4} />}
                  </label>
                );
              })}
          </div>
        );

      case "price":
        return (
          <div className="category-mobile-sheet-options">
            <div
              style={{
                padding: "8px",
                background: "#f8fafc",
                borderRadius: "8px",
                marginBottom: "8px",
                border: "1px solid #e2e8f0",
              }}
            >
              <span
                style={{
                  fontSize: "10px",
                  fontWeight: 800,
                  color: "#1e293b",
                  display: "block",
                  marginBottom: "6px",
                }}
              >
                Custom Range {priceRange ? `(₹${priceRange.min} - ₹${priceRange.max})` : ""}
              </span>
              <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                <input
                  type="number"
                  placeholder={priceRange ? `₹${priceRange.min}` : "Min"}
                  value={minPrice ?? ""}
                  onChange={(e) =>
                    onSetPrice(
                      e.target.value ? Number(e.target.value) : undefined,
                      maxPrice
                    )
                  }
                  style={{
                    flex: 1,
                    minWidth: 0,
                    height: "36px",
                    padding: "0 8px",
                    border: "1px solid #cbd5e1",
                    borderRadius: "6px",
                    fontSize: "11px",
                    outline: "none",
                    background: "#fff",
                  }}
                />
                <span style={{ fontSize: "11px", color: "#64748b" }}>–</span>
                <input
                  type="number"
                  placeholder={priceRange ? `₹${priceRange.max}` : "Max"}
                  value={maxPrice ?? ""}
                  onChange={(e) =>
                    onSetPrice(
                      minPrice,
                      e.target.value ? Number(e.target.value) : undefined
                    )
                  }
                  style={{
                    flex: 1,
                    minWidth: 0,
                    height: "36px",
                    padding: "0 8px",
                    border: "1px solid #cbd5e1",
                    borderRadius: "6px",
                    fontSize: "11px",
                    outline: "none",
                    background: "#fff",
                  }}
                />
                {(minPrice !== undefined || maxPrice !== undefined) && (
                  <button
                    type="button"
                    onClick={() => onSetPrice(undefined, undefined)}
                    style={{
                      height: "36px",
                      padding: "0 8px",
                      border: "1px solid #cbd5e1",
                      borderRadius: "6px",
                      background: "#fff",
                      fontSize: "10px",
                      fontWeight: 700,
                      color: "#173c72",
                      cursor: "pointer",
                    }}
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>

            {pricePresets
              .filter((p) => matchesSearch(p.label))
              .map((preset) => {
                const isSelected =
                  minPrice === preset.min && maxPrice === preset.max;
                return (
                  <label
                    key={preset.id}
                    className={`category-mobile-sheet-option ${
                      isSelected ? "active" : ""
                    }`}
                  >
                    <input
                      type="radio"
                      name="category-mobile-price-preset"
                      checked={isSelected}
                      onChange={() => {
                        if (isSelected) {
                          onSetPrice(undefined, undefined);
                        } else {
                          onSetPrice(preset.min, preset.max);
                        }
                      }}
                    />
                    <span>{preset.label}</span>
                  </label>
                );
              })}
          </div>
        );

      case "delivery":
        const deliveryOptions = [
          {
            id: "free",
            label: "Free Delivery",
            checked: freeDelivery,
            count: deliveryCounts?.freeDelivery,
          },
          {
            id: "fast",
            label: "Fast Delivery Available",
            checked: fastDelivery,
            count: deliveryCounts?.fastDelivery,
          },
          {
            id: "nextDay",
            label: "Next Day Delivery",
            checked: nextDayDelivery,
            count: deliveryCounts?.nextDayDelivery,
          },
        ];

        return (
          <div className="category-mobile-sheet-options">
            {deliveryOptions
              .filter((opt) => matchesSearch(opt.label))
              .map((opt) => (
                <label
                  key={opt.id}
                  className={`category-mobile-sheet-option ${
                    opt.checked ? "active" : ""
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={opt.checked}
                    onChange={() =>
                      onToggleDelivery(opt.id as "free" | "fast" | "nextDay")
                    }
                  />
                  <span>{opt.label}</span>
                  {opt.count !== undefined && (
                    <strong style={{ fontSize: "9px", color: "#64748b" }}>
                      ({opt.count})
                    </strong>
                  )}
                </label>
              ))}
          </div>
        );

      case "brands":
        return (
          <div className="category-mobile-sheet-options">
            {brands.length === 0 ? (
              <p style={{ fontSize: "11px", color: "#64748b", padding: "12px" }}>
                No brands found
              </p>
            ) : (
              brands
                .filter((brand) => matchesSearch(brand.name))
                .map((brand) => {
                  const isChecked = selectedBrands.includes(brand._id);
                  return (
                    <label
                      key={brand._id}
                      className={`category-mobile-sheet-option ${
                        isChecked ? "active" : ""
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => onToggleBrand(brand._id)}
                      />
                      <span>{brand.name}</span>
                      <strong style={{ fontSize: "9px", color: "#64748b" }}>
                        ({brand.count})
                      </strong>
                    </label>
                  );
                })
            )}
          </div>
        );

      case "ratings":
        const effectiveRatings =
          ratingBuckets.length > 0
            ? ratingBuckets
            : [
                { minRating: 4, count: 0, label: "4" },
                { minRating: 3, count: 0, label: "3" },
                { minRating: 2, count: 0, label: "2" },
                { minRating: 1, count: 0, label: "1" },
              ];

        return (
          <div className="category-mobile-sheet-options">
            {effectiveRatings.map((bucket) => {
              const isChecked = selectedRatings.includes(bucket.minRating);
              return (
                <label
                  key={bucket.minRating}
                  className={`category-mobile-sheet-option category-mobile-rating-option ${
                    isChecked ? "active" : ""
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => onToggleRating(bucket.minRating)}
                  />
                  <span className="category-mobile-rating-stars">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        size={14}
                        strokeWidth={1.8}
                        fill={star <= bucket.minRating ? "currentColor" : "none"}
                      />
                    ))}
                  </span>
                  <strong>
                    & Up {bucket.count > 0 ? `(${bucket.count})` : ""}
                  </strong>
                </label>
              );
            })}
          </div>
        );

      case "deals":
        return (
          <div className="category-mobile-sheet-options">
            {discountBuckets.length === 0 ? (
              <p style={{ fontSize: "11px", color: "#64748b", padding: "12px" }}>
                No deals or discounts found
              </p>
            ) : (
              discountBuckets
                .filter((d) => matchesSearch(d.label))
                .map((bucket) => {
                  const isChecked = selectedDiscounts.includes(bucket.minDiscount);
                  return (
                    <label
                      key={bucket.minDiscount}
                      className={`category-mobile-sheet-option ${
                        isChecked ? "active" : ""
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => onToggleDiscount(bucket.minDiscount)}
                      />
                      <span>{bucket.label}</span>
                      <strong style={{ fontSize: "9px", color: "#64748b" }}>
                        ({bucket.count})
                      </strong>
                    </label>
                  );
                })
            )}
          </div>
        );

      case "colors":
        return (
          <div className="category-mobile-sheet-options">
            {colors.length === 0 ? (
              <p style={{ fontSize: "11px", color: "#64748b", padding: "12px" }}>
                No colors found
              </p>
            ) : (
              colors
                .filter((c) => matchesSearch(c.value))
                .map((color) => {
                  const isChecked = selectedColors.includes(color.value);
                  return (
                    <label
                      key={color.value}
                      className={`category-mobile-sheet-option category-mobile-color-option ${
                        isChecked ? "active" : ""
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => onToggleColor(color.value)}
                      />
                      <span
                        className="category-mobile-filter-color-dot"
                        style={{ backgroundColor: color.value.toLowerCase() }}
                      />
                      <span>
                        {color.value}{" "}
                        <strong style={{ fontSize: "9px", color: "#64748b" }}>
                          ({color.count})
                        </strong>
                      </span>
                    </label>
                  );
                })
            )}
          </div>
        );

      case "materials":
        return (
          <div className="category-mobile-sheet-options">
            {materials.length === 0 ? (
              <p style={{ fontSize: "11px", color: "#64748b", padding: "12px" }}>
                No materials found
              </p>
            ) : (
              materials
                .filter((m) => matchesSearch(m.value))
                .map((mat) => {
                  const isChecked = selectedMaterials.includes(mat.value);
                  return (
                    <label
                      key={mat.value}
                      className={`category-mobile-sheet-option ${
                        isChecked ? "active" : ""
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => onToggleMaterial(mat.value)}
                      />
                      <span>{mat.value}</span>
                      <strong style={{ fontSize: "9px", color: "#64748b" }}>
                        ({mat.count})
                      </strong>
                    </label>
                  );
                })
            )}
          </div>
        );

      case "discounts":
        const standardDiscounts =
          discountBuckets.length > 0
            ? discountBuckets
            : [
                { minDiscount: 10, label: "10% Off or more", count: 0 },
                { minDiscount: 20, label: "20% Off or more", count: 0 },
                { minDiscount: 30, label: "30% Off or more", count: 0 },
                { minDiscount: 40, label: "40% Off or more", count: 0 },
                { minDiscount: 50, label: "50% Off or more", count: 0 },
              ];

        return (
          <div className="category-mobile-sheet-options">
            {standardDiscounts
              .filter((d) => matchesSearch(d.label))
              .map((item) => {
                const isChecked = selectedDiscounts.includes(item.minDiscount);
                return (
                  <label
                    key={item.minDiscount}
                    className={`category-mobile-sheet-option ${
                      isChecked ? "active" : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => onToggleDiscount(item.minDiscount)}
                    />
                    <span>{item.label}</span>
                    {item.count > 0 && (
                      <strong style={{ fontSize: "9px", color: "#64748b" }}>
                        ({item.count})
                      </strong>
                    )}
                  </label>
                );
              })}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div
      className="category-mobile-filter-layer"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <section
        className="category-mobile-filter-sheet"
        role="dialog"
        aria-modal="true"
        aria-label="Category filters"
      >
        <div className="category-mobile-filter-sheet-handle" />

        <div className="category-mobile-filter-sheet-header">
          <div>
            <span>REFINE RESULTS</span>
            <h2>Filters</h2>
          </div>

          <div className="category-mobile-filter-sheet-header-actions">
            <button
              type="button"
              className="category-mobile-clear-filters"
              onClick={onClearAll}
            >
              Clear All
            </button>

            <button
              type="button"
              className="category-mobile-filter-close"
              onClick={onClose}
              aria-label="Close filters"
            >
              <X size={19} strokeWidth={2} />
            </button>
          </div>
        </div>

        <div className="category-mobile-filter-sheet-content">
          {/* Left: Section navigation */}
          <nav
            className="category-mobile-filter-section-nav"
            aria-label="Filter categories"
          >
            {mobileCategoryFilterSections.map((section) => {
              const count = getMobileFilterCount(section.id);
              const isActive = activeSection === section.id;
              return (
                <button
                  type="button"
                  key={section.id}
                  className={isActive ? "active" : ""}
                  onClick={() => onSelectSection(section.id)}
                >
                  <span>{section.label}</span>
                  {count > 0 && <strong>{count}</strong>}
                </button>
              );
            })}
          </nav>

          {/* Right: Search + Options panel */}
          <div className="category-mobile-filter-options-panel">
            <div className="category-mobile-filter-options-title">
              <span>{activeSectionConfig?.label}</span>
              <strong>{getMobileFilterCount(activeSection)} selected</strong>
            </div>

            {/* Filter Search Input (except for sort and price where it's not needed) */}
            {activeSection !== "sort" && activeSection !== "price" && (
              <label className="category-mobile-filter-search">
                <Search size={18} strokeWidth={2} />
                <input
                  type="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={`Search ${activeSectionConfig?.label ?? "filters"}`}
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery("")}
                    aria-label="Clear search"
                  >
                    <X size={15} strokeWidth={2} />
                  </button>
                )}
              </label>
            )}

            <div className="category-mobile-filter-options-scroll">
              {renderOptions()}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="category-mobile-filter-footer">
          <button
            type="button"
            className="category-mobile-filter-reset"
            onClick={onClearAll}
          >
            Reset
          </button>

          <button
            type="button"
            className="category-mobile-filter-apply"
            onClick={onClose}
          >
            See {totalProductsCount} products
          </button>
        </div>
      </section>
    </div>
  );
};

export default CategoryMobileFilterSheet;
